import React, { useEffect, useState } from 'react';
import { theme } from '../../data/colors';

// M-Vitations logo gold tones - matched exactly
const GOLD_GRADIENT = {
  highlight: '#f8e7a0',  // Bright highlight from logo
  primary: '#b98b1f',    // Primary gold from logo
  shadow: '#826110',     // Deep shadow from logo
};

// Design tokens (faithful port from KpiRingNative)
const COLOR_MAP = {
  default: { stroke: 'url(#goldGradient)', accent: 'rgba(185,139,31,0.40)', track: '#E8DFC8', valueColor: theme.textPrimary, badgeColor: '#5f594e', useGradient: true },
  success: { stroke: 'url(#goldGradient)', accent: 'rgba(71,128,97,0.28)', track: '#E8DFC8', valueColor: theme.textPrimary, badgeColor: '#5d725f', useGradient: true },
  warning: { stroke: 'url(#goldGradient)', accent: 'rgba(150,113,53,0.28)', track: '#E8DFC8', valueColor: theme.textPrimary, badgeColor: '#7e6642', useGradient: true },
  danger:  { stroke: 'url(#goldGradient)', accent: 'rgba(140,86,82,0.26)', track: '#E8DFC8', valueColor: theme.textPrimary, badgeColor: '#815f5b', useGradient: true },
  info:    { stroke: 'url(#goldGradient)', accent: 'rgba(90,118,138,0.26)', track: '#E8DFC8', valueColor: theme.textPrimary, badgeColor: '#5f7381', useGradient: true },
  gold:    { stroke: 'url(#goldGradientPremium)', accent: 'rgba(185,139,31,0.45)', track: '#E8DFC8', valueColor: theme.textPrimary, badgeColor: '#b89543', useGradient: true },
};

const SIZE_MAP = {
  sm: { outer: 82,  strokeW: 8,  valueFontSize: 18, labelFontSize: 12, subtitleFontSize: 11, container: 102 },
  md: { outer: 112, strokeW: 10, valueFontSize: 28, labelFontSize: 14, subtitleFontSize: 11, container: 132 },
  lg: { outer: 136, strokeW: 12, valueFontSize: 34, labelFontSize: 16, subtitleFontSize: 11, container: 156 },
};

export default function KpiRing({
  label,
  value,
  displayValue,
  total,
  color = 'default',
  size = 'md',
  subtitle,
  innerSubtitle,
  isFocused = false,
  adults,
  children,
  showBreakdown = false,
  // Feb 2026: optional click handlers for the RSVP-Yes adult/child rows.
  // Only used by the RSVP-Yes ring on the dashboard. When null, behavior is
  // unchanged (no cursor change, no click reaction).
  onAdultClick = null,
  onChildClick = null,
}) {
  const c = COLOR_MAP[color];
  const s = SIZE_MAP[size];

  // Geometry
  const r = (s.outer - s.strokeW) / 2;
  const circumference = 2 * Math.PI * r;
  const pct = total != null ? Math.min(Math.max(value / total, 0), 1) : 1;
  const targetOffset = circumference * (1 - pct);

  // Animation state
  const [animOffset, setAnimOffset] = useState(circumference);

  useEffect(() => {
    // Animate to target offset
    const timeout = setTimeout(() => {
      setAnimOffset(targetOffset);
    }, 50);
    return () => clearTimeout(timeout);
  }, [targetOffset]);

  const shown = displayValue ?? value.toLocaleString();
  const center = s.outer / 2;
  const containerSize = s.outer + 20;
  const wrapperWidth = s.container + 28;
  const indicatorRadius = Math.max(6, r - s.strokeW * 0.92);
  const indicatorDash = Math.max(24, circumference * 0.18);
  const accentStroke = Math.max(1.4, s.strokeW * 0.16);
  const highlightStroke = Math.max(1.5, s.strokeW * 0.22);
  const isGold = color === 'gold';

  const wrapperStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 16,
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 2,
    paddingBottom: 2,
    width: wrapperWidth,
    transition: 'transform 180ms ease-out, opacity 180ms ease-out, box-shadow 180ms ease-out',
    transform: isFocused ? 'scale(1.04)' : 'scale(1)',
    opacity: isFocused ? 1 : 0.92,
  };

  const ringShellStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: containerSize,
    height: containerSize,
    borderRadius: containerSize / 2,
    backgroundColor: theme.bgCard,
    border: isGold ? '1px solid rgba(198,167,90,0.34)' : '1px solid rgba(198,167,90,0.28)',
    boxShadow: isFocused
      ? '0px 20px 40px rgba(0,0,0,0.28), 0px 50px 90px rgba(0,0,0,0.22), 0px 8px 16px rgba(0,0,0,0.18), 0px 0px 0px 1px rgba(198,167,90,0.14), inset 0px 2px 0px rgba(255,255,255,0.99), inset 0px -16px 32px rgba(18,18,18,0.06)'
      : isGold
        ? '0px 16px 32px rgba(0,0,0,0.25), 0px 44px 80px rgba(0,0,0,0.20), 0px 6px 14px rgba(0,0,0,0.16), 0px 0px 0px 1px rgba(198,167,90,0.12), inset 0px 2px 0px rgba(255,255,255,0.98), inset 0px -14px 28px rgba(18,18,18,0.05)'
        : '0px 14px 28px rgba(0,0,0,0.22), 0px 36px 70px rgba(0,0,0,0.18), 0px 6px 12px rgba(0,0,0,0.14), 0px 0px 0px 1px rgba(198,167,90,0.08), inset 0px 2px 0px rgba(255,255,255,0.97), inset 0px -12px 24px rgba(18,18,18,0.04)',
    overflow: 'hidden',
    position: 'relative',
  };

  const innerSurfaceStyle = {
    position: 'absolute',
    top: 10,
    right: 10,
    bottom: 10,
    left: 10,
    borderRadius: 999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: isGold
      ? 'linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(252,249,243,0.99) 50%, rgba(242,237,228,0.995) 100%)'
      : 'linear-gradient(180deg, rgba(255,255,255,0.998) 0%, rgba(251,248,242,0.988) 50%, rgba(244,240,232,0.992) 100%)',
  };

  const innerHighlightStyle = {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    borderRadius: 999,
    borderTop: '1px solid rgba(255,255,255,0.98)',
    borderBottom: isGold ? '1px solid rgba(198,167,90,0.18)' : '1px solid rgba(198,167,90,0.12)',
  };

  const svgStyle = {
    position: 'absolute',
    transform: 'rotate(-90deg)',
  };

  const centerOverlayStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingLeft: 8,
    paddingRight: 8,
  };

  const valueTextStyle = {
    fontWeight: 600,
    color: c.valueColor,
    textAlign: 'center',
    letterSpacing: -0.45,
    fontSize: s.valueFontSize,
  };

  const labelBlockStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    maxWidth: 168,
  };

  const labelTextStyle = {
    fontWeight: 600,
    color: theme.textPrimary,
    textAlign: 'center',
    lineHeight: '20px',
    letterSpacing: -0.18,
    fontSize: s.labelFontSize,
  };

  const subtitleTextStyle = {
    color: c.badgeColor,
    textAlign: 'center',
    marginTop: 7,
    lineHeight: '15px',
    letterSpacing: 0.2,
    fontSize: s.subtitleFontSize,
  };

  return (
    <div style={wrapperStyle}>
      <div style={ringShellStyle}>
        <div style={innerSurfaceStyle}>
          <div style={innerHighlightStyle} />
          <svg
            width={s.outer}
            height={s.outer}
            style={svgStyle}
          >
            {/* Gradient definitions - matched to M-Vitations logo gold */}
            <defs>
              {/* Standard gold gradient matching logo */}
              <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#f8e7a0" />
                <stop offset="30%" stopColor="#b98b1f" />
                <stop offset="50%" stopColor="#f8e7a0" />
                <stop offset="70%" stopColor="#b98b1f" />
                <stop offset="100%" stopColor="#826110" />
              </linearGradient>
              {/* Premium gold gradient with extra shimmer for gold color type */}
              <linearGradient id="goldGradientPremium" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#f8e7a0" />
                <stop offset="20%" stopColor="#b98b1f" />
                <stop offset="40%" stopColor="#f8e7a0" />
                <stop offset="50%" stopColor="#b98b1f" />
                <stop offset="60%" stopColor="#f8e7a0" />
                <stop offset="80%" stopColor="#b98b1f" />
                <stop offset="100%" stopColor="#826110" />
              </linearGradient>
              {/* Shimmer overlay effect */}
              <linearGradient id="goldShimmer" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="rgba(255,255,255,0)" />
                <stop offset="40%" stopColor="rgba(255,255,255,0)" />
                <stop offset="50%" stopColor="rgba(248,231,160,0.7)" />
                <stop offset="60%" stopColor="rgba(255,255,255,0)" />
                <stop offset="100%" stopColor="rgba(255,255,255,0)" />
              </linearGradient>
            </defs>
            {/* Track circle */}
            <circle
              cx={center}
              cy={center}
              r={r}
              fill="none"
              strokeWidth={s.strokeW}
              stroke={c.track}
            />
            {/* Highlight circle */}
            <circle
              cx={center}
              cy={center}
              r={r}
              fill="none"
              stroke="rgba(255,255,255,0.75)"
              strokeWidth={highlightStroke}
            />
            {/* Accent indicator circle */}
            <circle
              cx={center}
              cy={center}
              r={indicatorRadius}
              fill="none"
              stroke={c.accent}
              strokeWidth={accentStroke}
              strokeLinecap="round"
              strokeDasharray={`${indicatorDash} ${circumference}`}
              strokeDashoffset={-8}
            />
            {/* Progress circle with animation - using gradient */}
            <circle
              cx={center}
              cy={center}
              r={r}
              fill="none"
              strokeWidth={s.strokeW}
              stroke={c.stroke}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={animOffset}
              style={{ 
                transition: 'stroke-dashoffset 700ms cubic-bezier(0.33, 1, 0.68, 1)',
                filter: 'drop-shadow(0px 1px 2px rgba(212,175,55,0.4))',
              }}
            />
            {/* Shimmer highlight overlay on progress */}
            <circle
              cx={center}
              cy={center}
              r={r}
              fill="none"
              strokeWidth={s.strokeW * 0.5}
              stroke="url(#goldShimmer)"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={animOffset}
              style={{ 
                transition: 'stroke-dashoffset 700ms cubic-bezier(0.33, 1, 0.68, 1)',
                opacity: 0.7,
              }}
            />
          </svg>
          <div style={centerOverlayStyle}>
            {showBreakdown ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
              }}>
                {/* Adult row */}
                <div
                  data-testid="kpi-rsvp-yes-adult-row"
                  onClick={onAdultClick || undefined}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    cursor: onAdultClick ? 'pointer' : 'default',
                    padding: onAdultClick ? '2px 6px' : 0,
                    borderRadius: 4,
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={onAdultClick ? (e) => { e.currentTarget.style.background = 'rgba(185,139,31,0.10)'; } : undefined}
                  onMouseLeave={onAdultClick ? (e) => { e.currentTarget.style.background = 'transparent'; } : undefined}
                >
                  <svg width={s.valueFontSize * 0.5} height={s.valueFontSize * 0.7} viewBox="0 0 24 34" fill="none">
                    <defs>
                      <linearGradient id="iconGoldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#f8e7a0" />
                        <stop offset="50%" stopColor="#b98b1f" />
                        <stop offset="100%" stopColor="#826110" />
                      </linearGradient>
                    </defs>
                    <circle cx="12" cy="6" r="5" fill="url(#iconGoldGradient)"/>
                    <path d="M12 14c-5 0-9 3-9 7v6c0 1 1 2 2 2h14c1 0 2-1 2-2v-6c0-4-4-7-9-7z" fill="url(#iconGoldGradient)"/>
                  </svg>
                  <span style={{ color: 'rgba(0,0,0,0.35)', fontSize: s.valueFontSize * 0.45, fontWeight: 500 }}>:</span>
                  <span style={{ color: c.valueColor, fontSize: s.valueFontSize * 0.65, fontWeight: 600 }}>{adults ?? 0}</span>
                </div>
                {/* Child row */}
                <div
                  data-testid="kpi-rsvp-yes-child-row"
                  onClick={onChildClick || undefined}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    cursor: onChildClick ? 'pointer' : 'default',
                    padding: onChildClick ? '2px 6px' : 0,
                    borderRadius: 4,
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={onChildClick ? (e) => { e.currentTarget.style.background = 'rgba(185,139,31,0.10)'; } : undefined}
                  onMouseLeave={onChildClick ? (e) => { e.currentTarget.style.background = 'transparent'; } : undefined}
                >
                  <svg width={s.valueFontSize * 0.45} height={s.valueFontSize * 0.6} viewBox="0 0 22 30" fill="none">
                    <defs>
                      <linearGradient id="iconGoldGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#f8e7a0" />
                        <stop offset="50%" stopColor="#b98b1f" />
                        <stop offset="100%" stopColor="#826110" />
                      </linearGradient>
                    </defs>
                    <circle cx="11" cy="5" r="4" fill="url(#iconGoldGradient2)"/>
                    <path d="M11 12c-4 0-7 2.5-7 5.5v5c0 0.8 0.7 1.5 1.5 1.5h11c0.8 0 1.5-0.7 1.5-1.5v-5c0-3-3-5.5-7-5.5z" fill="url(#iconGoldGradient2)"/>
                    <path d="M6 18l5 6 5-6" fill="url(#iconGoldGradient2)"/>
                  </svg>
                  <span style={{ color: 'rgba(0,0,0,0.35)', fontSize: s.valueFontSize * 0.45, fontWeight: 500 }}>:</span>
                  <span style={{ color: c.valueColor, fontSize: s.valueFontSize * 0.65, fontWeight: 600 }}>{children ?? 0}</span>
                </div>
              </div>
            ) : innerSubtitle ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
              }}>
                <span style={valueTextStyle}>{shown}</span>
                <span style={{ 
                  fontSize: s.valueFontSize * 0.32, 
                  color: theme.textMuted, 
                  fontWeight: 500,
                  letterSpacing: '-0.2px',
                  textAlign: 'center',
                  lineHeight: 1.2,
                }}>{innerSubtitle}</span>
              </div>
            ) : (
              <span style={valueTextStyle}>{shown}</span>
            )}
          </div>
        </div>
      </div>
      <div style={labelBlockStyle}>
        <span style={labelTextStyle}>{label}</span>
        {subtitle != null && (
          <span style={subtitleTextStyle}>{subtitle}</span>
        )}
      </div>
    </div>
  );
}
